import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import api from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Badge } from '../components/common/Badge';
import { ProgressBar } from '../components/common/ProgressBar';
import {
  ChartBarIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  UsersIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { usePermissions } from '../hooks/usePermissions';
import { useNotification } from '../contexts/NotificationContext';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface ReportFilters {
  startDate: string;
  endDate: string;
  departmentId?: string;
  leaveTypeId?: string;
}

export const Reports = () => {
  const { t } = useTranslation();
  const { canViewReports, canExportReports } = usePermissions();
  const { showNotification } = useNotification();
  const [filters, setFilters] = useState<ReportFilters>({
    startDate: format(startOfMonth(subMonths(new Date(), 2)), 'yyyy-MM-dd'),
    endDate: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
  });

  // Redirect if user doesn't have permission
  if (!canViewReports) {
    return <Navigate to="/dashboard" replace />;
  }

  // Fetch departments and leave types for filters
  const { data: departments, isError: departmentsError } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      try {
        const response = await api.get('/departments');
        console.log('Departments response:', response.data);
        return response.data.departments || response.data.data || response.data || [];
      } catch (error) {
        console.error('Departments loading error:', error);
        return [];
      }
    },
    retry: 1
  });

  const { data: leaveTypes, isError: leaveTypesError } = useQuery({
    queryKey: ['leaveTypes'],
    queryFn: async () => {
      try {
        const response = await api.get('/leaves/types/all');
        console.log('Leave types response:', response.data);
        return response.data.leaveTypes || response.data.data || response.data || [];
      } catch (error) {
        console.error('Leave types loading error:', error);
        // Try alternative endpoint
        try {
          const fallbackResponse = await api.get('/leave-types');
          return fallbackResponse.data.leaveTypes || fallbackResponse.data || [];
        } catch (fallbackError) {
          console.error('Leave types fallback error:', fallbackError);
          return [];
        }
      }
    },
    retry: 1
  });

  // Fetch report data
  const { data: leaveReport, isLoading: loadingLeaveReport, isError: leaveReportError } = useQuery({
    queryKey: ['leaveReport', filters],
    queryFn: async () => {
      try {
        console.log('Fetching leave report with filters:', filters);
        const response = await api.get('/reports/leaves', { params: filters });
        console.log('Leave report response:', response.data);
        return response.data.data || response.data || {
          totalRequests: 0,
          totalDaysTaken: 0,
          activeEmployees: 0,
          byStatus: []
        };
      } catch (error) {
        console.error('Leave report error:', error);
        // Return mock data for development
        return {
          totalRequests: 0,
          totalDaysTaken: 0,
          activeEmployees: 0,
          byStatus: []
        };
      }
    },
    retry: 1,
    staleTime: 0,
    cacheTime: 0,
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: t('reports.errorTitle', 'Błąd ładowania raportu'),
        message: t('reports.errorMessage', 'Nie udało się załadować danych raportu. Spróbuj ponownie.')
      });
    }
  });

  const { data: balanceReport, isLoading: loadingBalanceReport, isError: balanceReportError } = useQuery({
    queryKey: ['balanceReport', filters],
    queryFn: async () => {
      try {
        console.log('Fetching balance report with filters:', filters);
        const response = await api.get('/reports/balances', { params: filters });
        console.log('Balance report response:', response.data);
        return response.data.data || response.data || { byLeaveType: [] };
      } catch (error) {
        console.error('Balance report error:', error);
        return { byLeaveType: [] };
      }
    },
    retry: 1,
    staleTime: 0,
    cacheTime: 0
  });

  const { data: usageReport, isLoading: loadingUsageReport, isError: usageReportError } = useQuery({
    queryKey: ['usageReport', filters],
    queryFn: async () => {
      try {
        console.log('Fetching usage report with filters:', filters);
        const response = await api.get('/reports/usage', { params: filters });
        console.log('Usage report response:', response.data);
        return response.data.data || response.data || { byDepartment: [] };
      } catch (error) {
        console.error('Usage report error:', error);
        return { byDepartment: [] };
      }
    },
    retry: 1,
    staleTime: 0,
    cacheTime: 0
  });

  const { data: employeeUsageReport, isLoading: loadingEmployeeUsageReport, isError: employeeUsageReportError } = useQuery({
    queryKey: ['employeeUsageReport', filters],
    queryFn: async () => {
      try {
        console.log('Fetching employee usage report with filters:', filters);
        // Make sure we're passing the right parameter names
        const params = {
          startDate: filters.startDate,
          endDate: filters.endDate,
          departmentId: filters.departmentId,
          leaveTypeId: filters.leaveTypeId
        };
        console.log('API params for employee usage:', params);
        const response = await api.get('/reports/employee-usage', { params });
        console.log('Employee usage report response:', response.data);
        return response.data.data || response.data || { employees: [], summary: {} };
      } catch (error) {
        console.error('Employee usage report error:', error);
        return { employees: [], summary: {} };
      }
    },
    retry: 1,
    staleTime: 0, // Disable cache to see changes immediately
    cacheTime: 0
  });

  const handleExport = async (reportType: string) => {
    try {
      console.log('Starting export for report type:', reportType);
      console.log('Export filters:', filters);
      console.log('Can export reports:', canExportReports);
      
      if (!canExportReports) {
        showNotification({
          type: 'error',
          title: t('reports.exportFailed', 'Eksport nieudany'),
          message: 'Nie masz uprawnień do eksportowania raportów.'
        });
        return;
      }

      showNotification({
        type: 'info',
        title: t('reports.exporting', 'Eksportowanie raportu'),
        message: t('reports.exportingMessage', 'Raport jest przygotowywany do pobrania...')
      });

      const response = await api.get(`/reports/${reportType}/export`, {
        params: filters,
        responseType: 'blob'
      });
      
      console.log('Export response received:', response.status, response.headers);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}-report-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showNotification({
        type: 'success',
        title: t('reports.exportComplete', 'Eksport zakończony'),
        message: t('reports.exportCompleteMessage', 'Raport został pobrany pomyślnie.')
      });
    } catch (error: any) {
      console.error('Export failed:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      showNotification({
        type: 'error',
        title: t('reports.exportFailed', 'Eksport nieudany'),
        message: error.response?.data?.message || error.message || t('reports.exportFailedMessage', 'Nie udało się wyeksportować raportu. Spróbuj ponownie.')
      });
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('reports.title', 'Raporty')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('reports.subtitle', 'Przeglądaj i eksportuj raporty zarządzania urlopami')}
        </p>
        
        {/* Quick Access to Monthly Report */}
        <div className="mt-4">
          <Link
            to="/reports/monthly-leave"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <DocumentDuplicateIcon className="h-5 w-5 mr-2" />
            Raport miesięczny dla księgowej
          </Link>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t('reports.filters', 'Filtry raportów')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input
              type="date"
              label={t('reports.from', 'Od')}
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            />
            <Input
              type="date"
              label={t('reports.to', 'Do')}
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('reports.department', 'Dział')}
              </label>
              <select
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                value={filters.departmentId || ''}
                onChange={(e) => setFilters({ ...filters, departmentId: e.target.value || undefined })}
              >
                <option value="">{t('reports.allDepartments', 'Wszystkie działy')}</option>
                {departments?.map((dept: any) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
                {departmentsError && (
                  <option disabled>Błąd ładowania działów</option>
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('reports.leaveType', 'Typ urlopu')}
              </label>
              <select
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                value={filters.leaveTypeId || ''}
                onChange={(e) => setFilters({ ...filters, leaveTypeId: e.target.value || undefined })}
              >
                <option value="">{t('reports.allLeaveTypes', 'Wszystkie typy urlopów')}</option>
                {leaveTypes?.map((type: any) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
                {leaveTypesError && (
                  <option disabled>Błąd ładowania typów urlopów</option>
                )}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentDuplicateIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500">
                    {t('reports.totalRequests', 'Łączna liczba wniosków')}
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {leaveReport?.totalRequests || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500">
                    {t('reports.totalDays', 'Łączna liczba dni')}
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {leaveReport?.totalDaysTaken || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UsersIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500">
                    {t('reports.activeEmployees', 'Aktywni pracownicy')}
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {leaveReport?.activeEmployees || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leave Requests by Status */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>{t('reports.requestsByStatus', 'Wnioski urlopowe według statusu')}</CardTitle>
            {canExportReports && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('leaves')}
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                {t('reports.export', 'Eksportuj')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loadingLeaveReport ? (
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {leaveReport?.byStatus?.map((item: any) => (
                <div key={item.status}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">{item.status}</span>
                    <span className="font-medium">{item.count} {t('reports.requests', 'wniosków')}</span>
                  </div>
                  <ProgressBar
                    value={(item.count / leaveReport.totalRequests) * 100}
                    variant={
                      item.status === 'approved' ? 'success' :
                      item.status === 'rejected' ? 'error' :
                      item.status === 'pending' ? 'warning' :
                      'primary'
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Leave Balance Summary */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>{t('reports.leaveBalance', 'Bilans urlopowy')}</CardTitle>
            {canExportReports && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('balances')}
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                {t('reports.export', 'Eksportuj')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loadingBalanceReport ? (
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('reports.leaveType', 'Typ urlopu')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('reports.totalAllocated', 'Łącznie przyznane')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('reports.totalUsed', 'Łącznie wykorzystane')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('reports.totalRemaining', 'Łącznie pozostałe')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('reports.usageRate', 'Wskaźnik wykorzystania')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {balanceReport?.byLeaveType?.map((item: any) => (
                    <tr key={item.leaveTypeId}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.leaveTypeName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.totalAllocated} {t('common.time.days', 'dni')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.totalUsed} {t('common.time.days', 'dni')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.totalRemaining} {t('common.time.days', 'dni')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={
                          item.usageRate > 80 ? 'error' :
                          item.usageRate > 60 ? 'warning' :
                          'success'
                        }>
                          {item.usageRate}%
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Department Usage */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>{t('reports.departmentUsage', 'Wykorzystanie urlopów według działów')}</CardTitle>
            {canExportReports && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('usage')}
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                {t('reports.export', 'Eksportuj')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loadingUsageReport ? (
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {usageReport?.byDepartment?.map((dept: any) => (
                <div key={dept.departmentId} className="border-b pb-4 last:border-0">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {dept.departmentName}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {dept.employeeCount} {t('reports.employees', 'pracowników')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {dept.totalDaysTaken}/{dept.totalDaysAllocated || dept.totalDaysTaken} dni wykorzystanych
                      </p>
                      <p className="text-sm text-gray-500">
                        {dept.averageDaysPerEmployee} {t('reports.avgPerEmployee', 'średnio/pracownik')}
                      </p>
                      <Badge variant={
                        (dept.usageRate || 0) >= 80 ? 'error' :
                        (dept.usageRate || 0) >= 50 ? 'warning' :
                        'success'
                      } size="sm">
                        {dept.usageRate || 0}% wykorzystania
                      </Badge>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Przyznane dni:</span>
                      <span className="ml-2 font-medium">{dept.totalDaysAllocated || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Wykorzystane dni:</span>
                      <span className="ml-2 font-medium">{dept.totalDaysTaken || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Pozostałe dni:</span>
                      <span className="ml-2 font-medium">{dept.totalDaysRemaining || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Wskaźnik wykorzystania:</span>
                      <span className="ml-2 font-medium">{dept.usageRate || 0}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employee Usage Report */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>{t('reports.employeeUsage', 'Wykorzystanie urlopów przez pracowników')}</CardTitle>
            {canExportReports && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('employee-usage')}
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                {t('reports.export', 'Eksportuj')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loadingEmployeeUsageReport ? (
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ) : (
            <div>
              {/* Summary */}
              {employeeUsageReport?.summary && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">{t('reports.totalEmployees', 'Liczba pracowników')}:</span>
                      <span className="ml-2 font-medium">{employeeUsageReport.summary.totalEmployees}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('reports.totalAvailable', 'Dostępne dni')}:</span>
                      <span className="ml-2 font-medium">{employeeUsageReport.summary.totalAvailableDays} dni</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('reports.totalRequested', 'Wnioskowane dni')}:</span>
                      <span className="ml-2 font-medium">{employeeUsageReport.summary.totalDaysRequested} dni</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('reports.totalApproved', 'Zatwierdzone dni')}:</span>
                      <span className="ml-2 font-medium">{employeeUsageReport.summary.totalDaysApproved} dni</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('reports.avgPerEmployee', 'Średnio/pracownik')}:</span>
                      <span className="ml-2 font-medium">{employeeUsageReport.summary.averageDaysPerEmployee} dni</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Compact Table View */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Pracownik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Dział
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Wypoczynkowy
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Na żądanie
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Chorobowy
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Okolicznościowy
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Opieka na dziecko
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Łącznie
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {employeeUsageReport?.employees?.map((employee: any) => {
                      // Create a map for quick lookup of leave types
                      const leaveTypeMap = (employee.leaveTypeDetails || []).reduce((acc: any, lt: any) => {
                        acc[lt.leaveTypeName] = lt;
                        return acc;
                      }, {});

                      const formatLeaveUsage = (leaveType: any) => {
                        if (!leaveType || leaveType.initialBalance === 0) return '0/0 (0%)';
                        const percentage = Math.round((leaveType.used / leaveType.initialBalance) * 100);
                        return `${leaveType.used}/${leaveType.initialBalance} (${percentage}%)`;
                      };

                      const getUsageColor = (leaveType: any) => {
                        if (!leaveType || leaveType.initialBalance === 0) return 'text-gray-500';
                        const percentage = (leaveType.used / leaveType.initialBalance) * 100;
                        if (percentage >= 80) return 'text-red-600 font-semibold';
                        if (percentage >= 50) return 'text-orange-600 font-medium';
                        return 'text-green-600';
                      };

                      return (
                        <tr key={employee.userId} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {employee.firstName} {employee.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {employee.email}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {employee.department}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-center ${getUsageColor(leaveTypeMap['Wypoczynkowy'])}`}>
                            {formatLeaveUsage(leaveTypeMap['Wypoczynkowy'])}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-center ${getUsageColor(leaveTypeMap['Na żądanie'])}`}>
                            {formatLeaveUsage(leaveTypeMap['Na żądanie'])}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-center ${getUsageColor(leaveTypeMap['Chorobowy'])}`}>
                            {formatLeaveUsage(leaveTypeMap['Chorobowy'])}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-center ${getUsageColor(leaveTypeMap['Okolicznościowy'])}`}>
                            {formatLeaveUsage(leaveTypeMap['Okolicznościowy'])}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm text-center ${getUsageColor(leaveTypeMap['Opieka na dziecko'])}`}>
                            {formatLeaveUsage(leaveTypeMap['Opieka na dziecko'])}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {(() => {
                              // Calculate total used days from leave type details
                              const totalUsed = (employee.leaveTypeDetails || [])
                                .reduce((sum: number, lt: any) => sum + parseFloat(lt.used || 0), 0);
                              const usagePercentage = employee.totalAvailableDays > 0 
                                ? Math.round((totalUsed / employee.totalAvailableDays) * 100)
                                : 0;
                              
                              return (
                                <>
                                  <div className="text-sm font-medium text-gray-900">
                                    {totalUsed}/{employee.totalAvailableDays}
                                  </div>
                                  <Badge variant={
                                    employee.totalAvailableDays === 0 ? 'secondary' :
                                    usagePercentage >= 80 ? 'error' :
                                    usagePercentage >= 50 ? 'warning' : 'success'
                                  } size="sm">
                                    {usagePercentage}%
                                  </Badge>
                                </>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};